import { apiService, API_ENDPOINTS, buildEndpoint } from "@/shared/services/api"
import type { APIResponse } from "@/shared/types"
import type { 
  CommissionRate, 
  CommissionRateStats, 
  CommissionRateFilters, 
  CommissionRateValidationResult
} from "../types"

export class CommissionRateService {
  /**
   * Get all commission rates with filters and pagination
   */
  static async getCommissionRates(filters?: CommissionRateFilters): Promise<{
    commissionRates: CommissionRate[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{
      commissionRates: CommissionRate[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>>(`${API_ENDPOINTS.hr.commissionRates}?${params.toString()}`)
    
    return response.data.data!
  }

  /**
   * Get commission rate by ID
   */
  static async getCommissionRateById(id: string): Promise<CommissionRate> {
    const response = await apiService.get<APIResponse<CommissionRate>>(
      buildEndpoint(API_ENDPOINTS.hr.commissionRatesById, { id })
    )
    return response.data.data!
  }

  /**
   * Get commission rate by organization, campaign, and agent
   */
  static async getCommissionRateByAgent(
    organizationId: string, 
    campaignId: string, 
    agentId: string
  ): Promise<CommissionRate> {
    const response = await apiService.get<APIResponse<CommissionRate>>(
      buildEndpoint(API_ENDPOINTS.hr.commissionRatesByAgentSpecific, { 
        organizationId, 
        campaignId, 
        agentId 
      })
    )
    return response.data.data!
  }

  /**
   * Create a new commission rate
   */
  static async createCommissionRate(data: {
    organizationId: string
    campaignId: string
    agentId: string
    commissionRate: number
    jobType: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT" | "MASTER_ORGANIZATION"
    enableSalesTier: boolean
    volumeTier1Min: number
    volumeTier1Max: number
    tier1PayRate: number
    volumeTier2Min: number
    volumeTier2Max: number
    tier2PayRate: number
    volumeTier3Min: number
    volumeTier3Max: number
    tier3PayRate: number
    overrideRate: number
    enableOverrideTier: boolean
    overrideVolumeTier1Min: number
    overrideVolumeTier1Max: number
    overrideTier1PayRate: number
    overrideVolumeTier2Min: number
    overrideVolumeTier2Max: number
    overrideTier2PayRate: number
    overrideVolumeTier3Min: number
    overrideVolumeTier3Max: number
    overrideTier3PayRate: number
    isActive: boolean
  }): Promise<{ success: boolean; message: string; data?: CommissionRate; warnings?: string[]; errors?: string[] }> {
    const response = await apiService.post<{ success: boolean; message: string; data?: CommissionRate; warnings?: string[]; errors?: string[] }>(
      API_ENDPOINTS.hr.commissionRates,
      data
    )
    return response.data
  }

  /**
   * Update commission rate
   */
  static async updateCommissionRate(
    id: string, 
    data: Partial<CommissionRate>
  ): Promise<CommissionRateValidationResult & { data?: CommissionRate }> {
    try {
      const response = await apiService.put<any>(
        buildEndpoint(API_ENDPOINTS.hr.commissionRatesById, { id }),
        data
      )
      
      
      // Handle successful response
      if (response.data.success) {
        return {
          isValid: true,
          errors: [],
          warnings: response.data.warnings || [],
          data: response.data.data
        }
      }
      
      // Handle validation errors returned as success=false in response
      return {
        isValid: false,
        errors: response.data.errors || [],
        warnings: response.data.warnings || [],
        data: undefined
      }
    } catch (error: any) {
      // Handle HTTP error responses (400, 500, etc.)
      if (error.response?.data) {
        const responseData = error.response.data
        return {
          isValid: false,
          errors: responseData.errors || [responseData.message || 'Update failed'],
          warnings: responseData.warnings || [],
          data: undefined
        }
      }
      
      // Handle network or other errors
      throw error
    }
  }

  /**
   * Delete commission rate
   */
  static async deleteCommissionRate(id: string): Promise<void> {
    await apiService.delete<APIResponse>(
      buildEndpoint(API_ENDPOINTS.hr.commissionRatesById, { id })
    )
  }

  /**
   * Get commission rate statistics
   */
  static async getCommissionRateStats(filters?: CommissionRateFilters): Promise<CommissionRateStats> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<CommissionRateStats>>(
      `${API_ENDPOINTS.hr.commissionRatesStats}?${params.toString()}`
    )
    return response.data.data!
  }

  /**
   * Get commission rates for a specific agent
   */
  static async getAgentCommissionRates(
    agentId: string, 
    filters?: Omit<CommissionRateFilters, 'agentId'>
  ): Promise<{
    commissionRates: CommissionRate[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{
      commissionRates: CommissionRate[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>>(
      `${buildEndpoint(API_ENDPOINTS.hr.commissionRatesByAgent, { agentId })}?${params.toString()}`
    )
    return response.data.data!
  }

  /**
   * Get commission rates for a specific organization
   */
  static async getOrganizationCommissionRates(
    organizationId: string, 
    filters?: Omit<CommissionRateFilters, 'organizationId'>
  ): Promise<{
    commissionRates: CommissionRate[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{
      commissionRates: CommissionRate[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>>(
      `${buildEndpoint(API_ENDPOINTS.hr.commissionRatesByOrganization, { organizationId })}?${params.toString()}`
    )
    return response.data.data!
  }

  /**
   * Get commission rates for a specific campaign
   */
  static async getCampaignCommissionRates(
    campaignId: string, 
    filters?: Omit<CommissionRateFilters, 'campaignId'>
  ): Promise<{
    commissionRates: CommissionRate[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{
      commissionRates: CommissionRate[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>>(
      `${buildEndpoint(API_ENDPOINTS.hr.commissionRatesByCampaign, { campaignId })}?${params.toString()}`
    )
    return response.data.data!
  }
}
